import React, { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  useGetBotGuildsQuery,
  useGetUserGuildsQuery,
  useGetWatchedGuildsQuery,
  useAddWatchedGuildMutation,
} from '../../features/guilds/guildsApi';
import { useAuthSlice } from '../../features/auth/authSlice';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { openExternalLink } from '../../utils/discord';
import type { Guild } from '../../types';

interface AddServerModalProps {
  onClose: () => void;
}

export const AddServerModal: React.FC<AddServerModalProps> = ({ onClose }) => {
  const authSlice = useAuthSlice();
  const [pendingGuildId, setPendingGuildId] = useState<string | null>(null);

  // Only fetch when modal is mounted (open)
  const {
    data: botGuilds = [],
    isLoading: botGuildsLoading,
    error: botGuildsError,
    refetch: refetchBotGuilds,
  } = useGetBotGuildsQuery(undefined, {
    skip: !authSlice.auth?.access_token,
  });

  const {
    data: userGuilds = [],
    isLoading: userGuildsLoading,
    error: userGuildsError,
  } = useGetUserGuildsQuery(undefined, {
    skip: !authSlice.auth?.access_token,
  });

  const { data: watchedGuilds = [] } = useGetWatchedGuildsQuery(undefined, {
    skip: !authSlice.auth?.access_token,
  });

  const [addWatchedGuild] = useAddWatchedGuildMutation();

  // Mutual guilds (bot + user) that aren't already watched
  const addableGuilds = useMemo(() => {
    const userGuildIds = new Set(userGuilds.map((g: Guild) => g.id));
    const watchedIds = new Set(watchedGuilds.map((g) => g.guild_id));
    return botGuilds.filter(
      (g: Guild) => userGuildIds.has(g.id) && !watchedIds.has(g.id)
    );
  }, [botGuilds, userGuilds, watchedGuilds]);

  const handleAdd = useCallback(
    async (guildId: string) => {
      setPendingGuildId(guildId);
      try {
        await addWatchedGuild({ guildId }).unwrap();
        toast.success('Server added to your tracked list.');
      } catch (err: unknown) {
        const error = err as { status?: number; data?: { error?: string } };
        if (error.status === 403) {
          toast.error("You're not an admin in that server.");
        } else if (error.status === 400) {
          toast.error(error.data?.error || 'Could not add this server.');
        } else {
          toast.error('Failed to add server. Please try again.');
        }
      } finally {
        setPendingGuildId(null);
      }
    },
    [addWatchedGuild]
  );

  const handleAddBotElsewhere = useCallback(() => {
    const inviteUrl = `https://discord.com/oauth2/authorize?client_id=${window.CLIENT_ID}&scope=bot&permissions=268435456`;
    openExternalLink(inviteUrl);
    // Refetch bot guilds shortly after — they may add the bot to a new server
    setTimeout(() => refetchBotGuilds(), 5000);
  }, [refetchBotGuilds]);

  const isLoading = botGuildsLoading || userGuildsLoading;
  const loadError = botGuildsError || userGuildsError;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface-container rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/20">
          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-on-surface">
              Add a Server to Track
            </h2>
            <p className="text-xs text-on-surface-variant mt-0.5">
              Pick a server you administer. We verify admin access once at this step.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-on-surface-variant hover:text-on-surface text-2xl leading-none px-2"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <LoadingSpinner message="Loading your servers..." />
          ) : loadError ? (
            <ErrorMessage message="Failed to load your Discord servers." />
          ) : addableGuilds.length === 0 ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-surface-container-low flex items-center justify-center mx-auto mb-4">
                <span className="material-symbols-outlined text-3xl text-on-surface-variant">
                  done_all
                </span>
              </div>
              <p className="text-on-surface font-semibold mb-1">
                No more servers to add
              </p>
              <p className="text-on-surface-variant text-sm max-w-sm mx-auto">
                Every server where you and the bot share membership is already in
                your tracked list. Add the bot to a new server to expand the list.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {addableGuilds.map((guild: Guild) => {
                const isPending = pendingGuildId === guild.id;
                return (
                  <li
                    key={guild.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-surface-container-low hover:bg-surface-container-high transition-colors"
                  >
                    {guild.icon ? (
                      <img
                        src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                        alt={guild.name}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-secondary-container text-on-secondary-container flex items-center justify-center font-semibold flex-shrink-0">
                        {guild.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-on-surface font-semibold truncate">
                        {guild.name}
                      </p>
                    </div>
                    <button
                      onClick={() => handleAdd(guild.id)}
                      disabled={isPending}
                      className="gradient-primary text-white text-sm font-semibold px-4 py-1.5 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-wait flex items-center gap-1.5"
                    >
                      {isPending ? (
                        <span className="material-symbols-outlined text-base animate-spin">
                          progress_activity
                        </span>
                      ) : (
                        <span className="material-symbols-outlined text-base">add</span>
                      )}
                      {isPending ? 'Adding...' : 'Add'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-outline-variant/20 flex items-center justify-between">
          <button
            onClick={handleAddBotElsewhere}
            className="text-sm text-primary-text hover:underline font-medium flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-base">open_in_new</span>
            Add bot to a different server
          </button>
          <button
            onClick={onClose}
            className="text-sm text-on-surface-variant hover:text-on-surface font-medium px-3 py-1.5"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
